// "Complete your profile" (spec/07 §3.2, owner 2026-10-08): right after a Google, Discord or Steam sign-up, before
// the player lands anywhere. The account already has a gamer tag (the server assigns one to every new account) and
// the provider's name; here the player keeps or changes the tag and writes the name venues and friends see.
//
//   The tag is checked as every tag is: the contract's rules here (3 to 20 letters, digits, "_" or "."), then the
//   server's `/v1/auth/update-user`, which refuses one somebody else holds and offers free ones. The name and the
//   finish go to `POST /v1/me/profile/complete`, after which `GET /v1/me` says `profileComplete` and the shell lets
//   the player in. Nothing else is asked: a date of birth or a real name is asked only where a venue's rule needs it
//   (spec/05 §3.2), at that venue.

import { ApiAuthError } from '@clavian/api-client';
import { isUsername } from '@clavian/contracts';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Chip, Skeleton } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';

import { useApi } from '../api/client';
import { queryKeys, useMe, useSession } from '../api/queries';
import { useProblem } from '../api/use-problem';
import { AuthLayout } from './AuthLayout';
import { Failure, TextInput } from './form-controls';

interface WelcomeValues {
  username: string;
  displayName: string;
}

export function WelcomePage(): ReactNode {
  const { t } = useTranslation('player');
  const session = useSession();
  const me = useMe();
  const user = session.data?.user;

  return (
    <AuthLayout title={t('welcome.title')} subtitle={t('welcome.intro')}>
      {user === undefined || me.data === undefined ? (
        <Skeleton shape="text" lines={4} />
      ) : (
        <WelcomeForm username={me.data.username ?? user.username ?? ''} displayName={me.data.displayName ?? user.name ?? ''} />
      )}
    </AuthLayout>
  );
}

function WelcomeForm({ username, displayName }: { readonly username: string; readonly displayName: string }): ReactNode {
  const { t } = useTranslation('player');
  const api = useApi();
  const problem = useProblem();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ from: '/welcome' });
  const [failure, setFailure] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<readonly string[]>([]);
  const form = useForm<WelcomeValues>({ defaultValues: { username, displayName } });

  const submit = form.handleSubmit(async (values) => {
    setFailure(null);
    setSuggestions([]);
    const tag = values.username.trim();
    try {
      if (tag !== username) await api.updateUser({ body: { username: tag } });
    } catch (error) {
      if (error instanceof ApiAuthError && error.code === 'USERNAME_IS_ALREADY_TAKEN') {
        const offered = error.body['suggestions'];
        setSuggestions(Array.isArray(offered) ? offered.filter((one): one is string => typeof one === 'string') : []);
        form.setError('username', { message: t('welcome.usernameTaken') });
        return;
      }
      if (error instanceof ApiAuthError && error.status === 400) {
        form.setError('username', { message: t('welcome.usernameInvalid') });
        return;
      }
      setFailure(problem.text(error, { fallback: t('welcome.failed') }));
      return;
    }
    try {
      const profile = await api.completeMyProfile({ body: { displayName: values.displayName.trim() } });
      queryClient.setQueryData(queryKeys.me, profile);
      await queryClient.invalidateQueries({ queryKey: queryKeys.session });
      await navigate({ to: search.redirect ?? '/home' });
    } catch (error) {
      const named = problem.fieldMessage(error, 'displayName');
      if (named !== undefined) form.setError('displayName', { message: named });
      else setFailure(problem.text(error, { fallback: t('welcome.failed') }));
    }
  });

  return (
    <form className="pw-form" onSubmit={(event) => void submit(event)} noValidate aria-label={t('welcome.title')}>
      <Failure message={failure} />
      <TextInput
        control={form.control}
        name="username"
        label={t('welcome.username')}
        description={t('welcome.usernameHint')}
        autoComplete="username"
        autoFocus
        rules={{
          required: t('signIn.required'),
          validate: (value: string) => isUsername(value.trim()) || t('welcome.usernameInvalid'),
        }}
      />
      {suggestions.length === 0 ? null : (
        <div className="clav-row" role="group" aria-label={t('welcome.suggestionsLabel')}>
          {suggestions.map((one) => (
            <Chip key={one} selected={false} onSelectedChange={() => form.setValue('username', one, { shouldValidate: true })}>
              {one}
            </Chip>
          ))}
        </div>
      )}
      <TextInput
        control={form.control}
        name="displayName"
        label={t('welcome.displayName')}
        description={t('welcome.displayNameHint')}
        autoComplete="nickname"
        maxLength={50}
        rules={{ required: t('signIn.required'), validate: (value: string) => value.trim() !== '' || t('signIn.required') }}
      />
      <Button type="submit" variant="primary" block busy={form.formState.isSubmitting}>
        {t('welcome.submit')}
      </Button>
    </form>
  );
}
